import { Injectable } from '@angular/core';
import html2pdf from 'html2pdf.js';
import { ApiService } from '../../services/api.service';
import { ConversationJson, ConversationMessage } from './chat-bot.models';
@Injectable({
  providedIn: 'root'
})
export class ChatBotHelperService {

  constructor(private apiService: ApiService) { }


  private formatMessageContent(content: string, inline: boolean = false): string {
    if (!content) return '';

    // normalize CRLF -> LF
    content = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

    // collapse multiple blank lines into a single newline
    content = content.replace(/\n\s*\n+/g, '\n');

    if (!inline) {
      return content
        .replace(/^# (.*)$/gm, '<div class="pdf-h1">$1</div>')
        .replace(/^## (.*)$/gm, '<div class="pdf-h2">$1</div>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br/>');
    }

    // inline mode: avoid block-level tags
    return content
      .replace(/^# (.*)$/gm, '<strong>$1</strong>')
      .replace(/^## (.*)$/gm, '<strong>$1</strong>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>');
  }

  getMessageContent(msg: any): string {
    let content:any = this.formatMessageContent(msg.message || '');
    return content;
  }

  private buildTableHtml(msg: any): string {

    let html = `
      <div style=" width:100%; overflow:hidden; page-break-inside:auto; ">
        <table style=" width:100%; max-width:100%; border-collapse:collapse; table-layout:fixed; margin-top:10px; font-size:10px; page-break-inside:auto; " >
          <thead>
            <tr>`;

    msg.ui.columns.forEach((col: any) => {

      html += `
        <th
          style=" border:1px solid #000; padding:6px; text-align:left; word-break:break-word; overflow-wrap:break-word; white-space:normal; background:#f5f5f5; ">
          ${col.header}
        </th>
      `;
    });

    html += `</tr> </thead> <tbody> `;

    msg.ui.data.forEach((row: any) => {

      html += `<tr style=" page-break-inside:avoid !important; break-inside:avoid !important; " > `;

      msg.ui.columns.forEach((col: any) => {

        html += `
          <td style=" border:1px solid #000; padding:6px; word-break:break-word; overflow-wrap:break-word; white-space:normal; vertical-align:top; " >
            ${row[col.key] ? this.formatMessageContent(String(row[col.key]), true) : ''}
          </td>
        `;
      });

        html += `</tr>`;
    });

    html += ` </tbody> </table> </div> `;

    return html;
  }

  buildConversationJson(messages: any[]): ConversationJson {
    const exported: ConversationMessage[] = messages.map((msg) => ({
      sender: msg.sender as 'user' | 'assistant',
      message: this.getMessageContent(msg),
      uiType: msg.uiType ?? null,
      timestamp: msg.timestamp ? new Date(msg.timestamp) : null,
      formatTime: msg.formatTime ?? ''
    }));

    return {
      exportedAt: new Date().toISOString(),
      messages: exported
    };
  }

  async generateConversationPdf(messages: any[]): Promise<Blob> {
    const html = this.buildEnhancedConversationHtml(messages);
    const container = document.createElement('div');
    container.innerHTML = html;

    const options = {
      margin: [10, 10, 10, 10],
      pagebreak: { mode: ['css', 'legacy'],avoid: ['tr','.pdf-message', '.pdf-bubble'] },
      filename: 'chat-conversation.pdf',
      image: { type: 'png', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    const pdfBlob: Blob = await html2pdf()
      .set(options)
      .from(container)
      .outputPdf('blob');

    if (!(pdfBlob instanceof Blob)) {
      throw new Error('PDF generation failed');
    }

    return pdfBlob;
  }

  async sendConversationToCsm(messages: any[]): Promise<FormData> {
    const pdfBlob = await this.generateConversationPdf(messages);

    const formData = new FormData();
    formData.append('file', pdfBlob, 'chat-conversation.pdf');
    // const pdfUrl = URL.createObjectURL(pdfBlob);
    // window.open(pdfUrl, '_blank');  

    return formData;
  }

  private buildEnhancedConversationHtml(messages: any[]): string {
    let html = `
      <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; color: #333; font-size: 13px; }
          .pdf-header { border-bottom: 2px solid #1976d2; padding-bottom: 12px; margin-bottom: 20px; }
          .pdf-header h2 { margin: 0; color: #1976d2; font-size: 18px; }
          .pdf-header p  { margin: 4px 0 0; font-size: 11px; color: #666; }
          .pdf-message { margin-bottom: 18px; display: flex; flex-direction: column; }
          .pdf-message-user      { align-items: flex-end; }
          .pdf-message-assistant { align-items: flex-start; }
          .pdf-sender-label { font-size: 10px; font-weight: bold; color: #666; margin-bottom: 3px; }
          .pdf-bubble { max-width: 72%; padding: 10px 14px; word-wrap: break-word; }
          .pdf-bubble-user      { background: #1976d2; color: #fff;  border-radius: 12px 12px 0 12px; }
          .pdf-bubble-assistant { background: #f0f0f0; color: #333; border-radius: 12px 12px 12px 0; }
          .pdf-timestamp { font-size: 9px; color: #aaa; margin-top: 3px; }
          .pdf-table { width: 100%; border-collapse: collapse; margin-top: 10px; page-break-inside: auto; }
          .pdf-table thead { display: table-header-group; }
          .pdf-table tr  { page-break-inside: avoid; page-break-after: auto; }
          .pdf-table td, .pdf-table th { border: 1px solid #ccc; padding: 6px 8px; }
        </style>
      </head>
      <body>
        
      `;

    messages.forEach((msg) => {
      const isUser     = msg.sender === 'user';
      const senderLabel = isUser ? 'You' : 'Iris';
      const bubbleClass = isUser ? 'pdf-bubble-user' : 'pdf-bubble-assistant';
      const msgClass    = isUser ? 'pdf-message-user' : 'pdf-message-assistant';
      const content     = this.getMessageContent(msg);
      const ts          = msg.formatTime ? (msg.formatTime.split(' - ')[1] || msg.formatTime) : '';

      html += `
        <div class="pdf-message ${msgClass}">
          <div class="pdf-sender-label">${senderLabel}</div>
          <div class="pdf-bubble ${bubbleClass}">
            ${content ? content.replace(/\n/g, '<br>') : ''}
            ${msg.uiType === 'table' ? this.buildTableHtml(msg) : ''}
          </div>
          ${ts ? `<div class="pdf-timestamp">${ts}</div>` : ''}
        </div>
      `;
    });

    html += `</body>\n</html>`;
    return html;
  }

}
